import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';
import { Environment } from '../../../environment';
import { NfeService } from '../Financeiro/fiscal';

// MDF-e (Manifesto Eletrônico de Documentos Fiscais) — GET /mdfe do backend de
// MDF-e (backendsiacmdfe, controller/providers "mdfe"), usado como está.

// Valores de `tipo` que o GET /mdfe entende (providers/mdfe/GetAll.ts):
//  - 'm.dhemi' → período de emissão em datainicial/datafinal (YYYY-MM-DD;
//    vazios = todas);
//  - 'm.nmdf'  → filtro = número (vazio = todos);
//  - 'm.idparceiro' → filtro = idparceiro do contratante (autocomplete;
//    vazio = todos);
//  - 'p.nome'  → filtro = nome do parceiro (contratante).
export type TTipoBuscaMdfe = 'm.dhemi' | 'm.nmdf' | 'm.idparceiro' | 'p.nome';

// Campos que o GET /mdfe devolve.
export interface IListagemMdfe {
  idmdfe: string;
  dhemi: string;
  nmdf: number;
  nome: string | null;
  cpf: string | null;
  status: number | null;
  vcarga: number;
}

type TMdfeComTotalCount = {
  data: IListagemMdfe[];
  totalCount: number;
};

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const mensagemDeErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) return new Error(axiosError.response.data?.errors?.default || 'Erro desconhecido');
    if (axiosError.request) return new Error('Nenhuma resposta do servidor');
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

const getAll = async (
  page = 1, filter = '', tipo: TTipoBuscaMdfe = 'm.dhemi', limit = Environment.LIMITE_DE_LINHAS, datainicial = '', datafinal = '',
): Promise<TMdfeComTotalCount | Error> => {
  try {
    const { data, headers } = await Api.get('/mdfe', { params: { page, limit, filter, tipo, datainicial, datafinal } });
    if (Array.isArray(data)) {
      return { data, totalCount: Number(headers['x-total-count'] || data.length) };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    return mensagemDeErro(error);
  }
};

// Situação do MDF-e pelo campo `status`, no mesmo padrão da NF-e, mais o
// "Encerrado" do MDF-e: vazio/1 = Pendente · 2 = Autorizado · 3 = Cancelado ·
// 4 = Encerrado.
export type TSituacaoMdfe = 'pendente' | 'autorizado' | 'cancelado' | 'encerrado';
export const situacaoMdfe = (status: number | null | undefined): TSituacaoMdfe => {
  switch (Number(status)) {
    case 2: return 'autorizado';
    case 3: return 'cancelado';
    case 4: return 'encerrado';
    default: return 'pendente';
  }
};

// --- Criação: POST /mdfe -----------------------------------------------------
// Corpo = [cabeçalho, documentos[], seguros[], veículos[]] (controller/mdfe/
// Create.ts). Campos que o banco guarda como lista (text[]) e o controller
// valida como texto vão no formato de lista do Postgres — ver paraListaPg.
export interface INovoMdfe {
  idmdfe: string;
  cuf: number;
  tpamb: number;
  // Tipo do emitente (tpEmit): 1 = prestador de serviço de transporte,
  // 2 = carga própria, 3 = prestador que emitirá CT-e globalizado.
  tpemit: number;
  tptrasnp: number;
  serie: number;
  // nmdf não vai: o servidor gera o número ao salvar (Create não valida nmdf).
  nmdf?: number;
  cmdf: number;
  dhemi: string;
  dhiniviagem: string;
  // Contratante saiu da tela (no lugar dele: tpemit) — não é mais enviado.
  idparceiro?: string;
  modal: string;
  tpemis: number;
  infmuncarrega?: string;
  infpercurso?: string;
  idveiculotracado: string | null;
  iddocs: string | null;
  idseguro: string | null;
  idinfantt: null;
  qcte: number;
  qnfe: number;
  qmdf: number;
  vcarga: number;
  cunid: string;
  qcarga: number;
  ufni: string;
  uffim: string;
}

export interface INovoMdfeDocs {
  iddocs: string;
  idmdfe: string;
  cmundescarga: string;
  xmundescarga: string;
  infcte?: string;
  inffe?: string;
}

export interface INovoMdfeSeguro {
  idseguro: string;
  idmdfe: string;
  infresp?: string;
  infseg?: string;
  naver?: string;
}

export interface INovoMdfeVeiculo {
  idveiculos: string;
  idmdfe: string;
  cint: number;
  placa: string;
  renavam?: string;
  tara: string;
  capkg?: string;
  capm3?: string;
  condutor: string;
  tprod: string;
  tcar: string;
  uf?: string;
}

// Lista do Postgres em texto ({"a","b"}): passa na validação do controller
// (campos string) e o banco aceita na coluna text[]. Objetos viram JSON — é o
// formato que o módulo de MDF-e usa (ex.: condutor {xnome, cpf}, NF-e {chnfe}).
export const paraListaPg = (itens: unknown[]): string =>
  `{${itens
    .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
    .map((texto) => `"${texto.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
    .join(',')}}`;

const create = async (
  mdfe: INovoMdfe, docs: INovoMdfeDocs[], seguros: INovoMdfeSeguro[], veiculos: INovoMdfeVeiculo[],
): Promise<void | Error> => {
  try {
    // TODO: log temporário pra conferir o corpo do POST /mdfe — remover depois.
    console.log('[MDF-e] POST /mdfe', { mdfe, docs, seguros, veiculos });
    console.log('[MDF-e] POST /mdfe (JSON)', JSON.stringify([mdfe, docs, seguros, veiculos], null, 2));
    await Api.post('/mdfe', [mdfe, docs, seguros, veiculos]);
  } catch (error) {
    console.log('[MDF-e] erro no POST /mdfe', axios.isAxiosError(error) ? error.response?.data : error);
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      // Erro de validação do controller: { errors: { campo: mensagem } }.
      const erros = (error.response.data as { errors?: Record<string, string> })?.errors;
      if (erros && !erros.default) return new Error(Object.entries(erros).map(([campo, msg]) => `${campo}: ${msg}`).join(' | '));
    }
    return mensagemDeErro(error);
  }
};

// --- NF-e do sistema para o MDF-e ---------------------------------------------
// Lê o XML AUTORIZADO da NF-e (GET /xmlnfe/:id, mesma rota do botão de baixar
// XML na tela de NF-e) e tira o que o MDF-e precisa: a chave de acesso (a
// listagem/consulta de NF-e não devolvem a chave), o valor, o peso bruto, o
// município do destinatário (descarregamento) e o do emitente (carregamento).
// Só NF-e autorizada tem esse XML.
export interface INfeParaMdfe {
  chave: string;
  numero: string;
  valor: number;
  pesoBruto: number;
  destino: { codigo: string; nome: string; uf: string } | null;
  origem: { codigo: string; nome: string; uf: string } | null;
}

const textoDe = (pai: Element | Document | null | undefined, tag: string) =>
  pai?.getElementsByTagName(tag)[0]?.textContent?.trim() || '';

export const lerNfeParaMdfe = async (idnfe: string): Promise<INfeParaMdfe | Error> => {
  const arquivo = await NfeService.baixarXml(idnfe);
  if (arquivo instanceof Error) return arquivo;
  const xml = new DOMParser().parseFromString(await arquivo.text(), 'application/xml');
  if (xml.getElementsByTagName('parsererror').length) return new Error('O XML da nota não pôde ser lido.');

  const infNFe = xml.getElementsByTagName('infNFe')[0];
  const chave = (textoDe(xml, 'chNFe') || (infNFe?.getAttribute('Id') || '')).replace(/\D/g, '');
  if (chave.length !== 44) return new Error('Chave de acesso não encontrada no XML da nota.');

  const enderecoDe = (grupo: string) => {
    const end = xml.getElementsByTagName(grupo)[0];
    const codigo = textoDe(end, 'cMun');
    return codigo ? { codigo, nome: textoDe(end, 'xMun'), uf: textoDe(end, 'UF') } : null;
  };
  const pesoBruto = Array.from(xml.getElementsByTagName('vol'))
    .reduce((soma, vol) => soma + (Number(textoDe(vol, 'pesoB')) || 0), 0);

  return {
    chave,
    numero: textoDe(xml.getElementsByTagName('ide')[0], 'nNF'),
    valor: Number(textoDe(xml.getElementsByTagName('ICMSTot')[0], 'vNF')) || 0,
    pesoBruto,
    destino: enderecoDe('enderDest'),
    origem: enderecoDe('enderEmit'),
  };
};

// --- Consulta: GET /mdfe/:id ---------------------------------------------------
// Devolve a linha do mdfe com as listas filhas em mdfedocs, mdfeseguro e
// mdfveiculos (providers/mdfe/GetById.ts). As colunas text[] chegam como lista
// de textos (ou no formato {"..."} do Postgres) — ler com daListaPg.
type TListaPg = string[] | string | null | undefined;
export interface IMdfeCompleto extends Omit<INovoMdfe, 'infmuncarrega' | 'infpercurso'> {
  nmdf?: number;
  status?: number | null;
  infmuncarrega?: TListaPg;
  infpercurso?: TListaPg;
  mdfedocs?: { iddocs: string; cmundescarga: string; xmundescarga: string; inffe?: TListaPg; infcte?: TListaPg }[];
  mdfeseguro?: { idseguro: string; infresp?: TListaPg; infseg?: TListaPg; naver?: TListaPg }[];
  mdfveiculos?: { idveiculos: string; placa: string; renavam?: string; tara: string; capkg?: string; capm3?: string; condutor?: TListaPg; tprod: string; tcar: string; uf?: string }[];
}

// Inverso de paraListaPg: aceita a lista já convertida pelo driver ou o texto
// {"a","b"}; cada item que for JSON vira objeto.
export const daListaPg = <T = unknown>(valor: TListaPg): T[] => {
  if (!valor) return [];
  let itens: string[] = [];
  if (Array.isArray(valor)) {
    itens = valor;
  } else {
    const corpo = valor.trim().replace(/^\{/, '').replace(/\}$/, '');
    const regex = /"((?:[^"\\]|\\.)*)"|([^,]+)/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(corpo))) itens.push(m[1] !== undefined ? m[1].replace(/\\(.)/g, '$1') : m[2].trim());
  }
  return itens.map((item) => {
    try { return JSON.parse(item) as T; } catch { return item as unknown as T; }
  });
};

const getById = async (id: string): Promise<IMdfeCompleto | Error> => {
  try {
    const { data } = await Api.get(`/mdfe/${id}`);
    if (data && data.idmdfe) return data;
    return new Error('MDF-e não encontrado.');
  } catch (error) {
    return mensagemDeErro(error);
  }
};

// PUT /mdfe/:id — mesmo corpo do POST (controller/mdfe/UpdateById.ts).
const updateById = async (
  id: string, mdfe: INovoMdfe, docs: INovoMdfeDocs[], seguros: INovoMdfeSeguro[], veiculos: INovoMdfeVeiculo[],
): Promise<void | Error> => {
  try {
    // TODO: log temporário pra conferir o corpo do PUT /mdfe — remover depois.
    console.log('[MDF-e] PUT /mdfe/' + id, JSON.stringify([mdfe, docs, seguros, veiculos], null, 2));
    await Api.put(`/mdfe/${id}`, [mdfe, docs, seguros, veiculos]);
  } catch (error) {
    console.log('[MDF-e] erro no PUT /mdfe', axios.isAxiosError(error) ? error.response?.data : error);
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      const erros = (error.response.data as { errors?: Record<string, string> })?.errors;
      if (erros && !erros.default) return new Error(Object.entries(erros).map(([campo, msg]) => `${campo}: ${msg}`).join(' | '));
    }
    return mensagemDeErro(error);
  }
};

// Encerramento na SEFAZ (evento 110112) de um MDF-e autorizado — GET
// /encerramento/:id (controller/mdfe/Encerra.ts). Encerra no município de
// descarregamento e, aceito, o MDF-e passa a Encerrado (status 4).
const encerrar = async (id: string): Promise<void | Error> => {
  try {
    await Api.get(`/encerramento/${id}`, { timeout: 60000 });
  } catch (error) {
    return mensagemDeErro(error);
  }
};

export const MdfeService = {
  getAll,
  getById,
  create,
  updateById,
  encerrar,
  lerNfeParaMdfe,
};
